import { useEffect, useRef, useState, FormEvent } from 'react';
import Button from '@components/Button';
import {
  listVisionBoardTemplates,
  createVisionBoardTemplate,
  updateVisionBoardTemplate,
  deleteVisionBoardTemplate,
  type NewVisionBoardTemplate,
} from '@lib/visionBoardTemplates';
import type { VisionBoardTemplate } from '@lib/api';

type TemplateFormState = {
  title: string;
  description: string;
  preview_image_url: string;
  image_url: string;
  sort_order: number;
  active: boolean;
};

const blankForm: TemplateFormState = {
  title: '',
  description: '',
  preview_image_url: '',
  image_url: '',
  sort_order: 0,
  active: true,
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminVisionBoardTemplates() {
  const [templates, setTemplates] = useState<VisionBoardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<VisionBoardTemplate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState<VisionBoardTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TemplateFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listVisionBoardTemplates(true);
        if (isMounted) setTemplates(data);
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error
              ? err.message
              : String(err ?? 'Unable to load templates')
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const openAdd = () => {
    setEditing(null);
    setFormData(blankForm);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (template: VisionBoardTemplate) => {
    setEditing(template);
    setFormError(null);
    setFormData({
      title: template.title,
      description: template.description ?? '',
      preview_image_url: template.preview_image_url ?? '',
      image_url: template.image_url ?? '',
      sort_order: template.sort_order,
      active: template.active,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
  };

  const openDelete = (template: VisionBoardTemplate) => {
    setDeleting(template);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDelete = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeleting(null);
    setDeleteError(null);
  };

  const handleInputChange = (
    field: keyof TemplateFormState,
    value: string | boolean | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    try {
      const title = formData.title.trim();
      if (!title) throw new Error('Title is required');

      const payload: NewVisionBoardTemplate = {
        title,
        description: formData.description.trim() || null,
        preview_image_url: formData.preview_image_url.trim() || null,
        image_url: formData.image_url.trim() || null,
        sort_order: formData.sort_order,
        active: formData.active,
      };

      if (editing) {
        const updated = await updateVisionBoardTemplate(editing.id, payload);
        setTemplates((prev) =>
          prev
            .map((item) => (item.id === updated.id ? updated : item))
            .sort((a, b) => a.sort_order - b.sort_order)
        );
      } else {
        const created = await createVisionBoardTemplate(payload);
        setTemplates((prev) =>
          [...prev, created].sort((a, b) => a.sort_order - b.sort_order)
        );
      }
      setIsModalOpen(false);
      setEditing(null);
      setFormData(blankForm);
    } catch (err) {
      if (err instanceof Error) {
        setFormError(err.message);
      } else if (err && typeof err === 'object') {
        const e = err as Record<string, unknown>;
        const parts: string[] = [];
        if (e.message) parts.push(String(e.message));
        if (e.details) parts.push(String(e.details));
        if (e.hint) parts.push(String(e.hint));
        setFormError(parts.length ? parts.join(' - ') : JSON.stringify(e));
      } else {
        setFormError(String(err ?? 'Failed to save template'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteVisionBoardTemplate(deleting.id);
      setTemplates((prev) => prev.filter((b) => b.id !== deleting.id));
      setShowDeleteModal(false);
      setDeleting(null);
    } catch (err) {
      if (err instanceof Error) {
        setDeleteError(err.message);
      } else if (err && typeof err === 'object') {
        const e = err as Record<string, unknown>;
        const parts: string[] = [];
        if (e.message) parts.push(String(e.message));
        if (e.details) parts.push(String(e.details));
        if (e.hint) parts.push(String(e.hint));
        setDeleteError(parts.length ? parts.join(' - ') : JSON.stringify(e));
      } else {
        setDeleteError(String(err ?? 'Failed to delete'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Vision Board Templates
          </h1>
          <p className="text-lg text-gray-600">
            Manage pre-made vision board templates for users.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {templates.length}
              </span>{' '}
              total
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div />
            <Button
              type="button"
              onClick={openAdd}
              className="item-selected inline-flex items-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Create Template
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No vision board templates yet. Create one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 lg:hidden">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-4">
                          {template.preview_image_url ? (
                            <div
                              className="h-16 w-24 overflow-hidden rounded-lg border border-gray-200 bg-cover bg-center"
                              style={{
                                backgroundImage: `url(${template.preview_image_url})`,
                              }}
                            />
                          ) : (
                            <div className="flex h-16 w-24 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-400">
                              No Preview
                            </div>
                          )}
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                              {template.title}
                            </h2>

                            <div className="mt-2 flex flex-wrap gap-2">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${template.active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
                              >
                                {template.active ? 'Active' : 'Not active'}
                              </span>
                              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                                Order: {template.sort_order}
                              </span>
                            </div>
                            <div className="mt-2 text-xs uppercase tracking-wide text-gray-500">
                              Created {formatDate(template.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                          <ActionsMenu
                            id={template.id}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => openEdit(template),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDelete(template),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Preview</th>
                          <th className="px-4 py-3">Title</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Sort Order</th>
                          <th className="px-4 py-3">Created</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map((template) => (
                          <tr
                            key={template.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top text-gray-900">
                              {template.preview_image_url ? (
                                <div
                                  className="h-12 w-20 overflow-hidden rounded-lg border border-gray-200 bg-cover bg-center"
                                  style={{
                                    backgroundImage: `url(${template.preview_image_url})`,
                                  }}
                                />
                              ) : (
                                <div className="flex h-12 w-20 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-[10px] text-gray-400">
                                  No Preview
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <div className="font-semibold">
                                {template.title}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${template.active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                              >
                                {template.active ? 'Active' : 'Not active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {template.sort_order}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {formatDate(template.created_at)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap justify-end gap-3">
                                <ActionsMenu
                                  id={template.id}
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEdit(template),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => openDelete(template),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close template modal"
              disabled={saving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editing ? 'Edit Template' : 'Create Template'}
            </h2>
            <p className="text-sm text-gray-500">
              {editing
                ? 'Update this vision board template.'
                : 'Create a new pre-made vision board template.'}
            </p>
            {formError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSave}
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="template-title"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Title
                    </label>
                    <input
                      id="template-title"
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleInputChange('title', val);
                      }}
                      required
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="template-description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="template-description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange('description', e.target.value)
                    }
                    rows={2}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="template-image-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Image URL (full background)
                  </label>
                  <input
                    id="template-image-url"
                    type="text"
                    value={formData.image_url}
                    onChange={(e) =>
                      handleInputChange('image_url', e.target.value)
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. /pictures/templates/sunset.jpg"
                  />
                </div>

                <div>
                  <label
                    htmlFor="template-preview-image-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Preview Image URL
                  </label>
                  <input
                    id="template-preview-image-url"
                    type="text"
                    value={formData.preview_image_url}
                    onChange={(e) =>
                      handleInputChange('preview_image_url', e.target.value)
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="template-sort-order"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Sort Order
                    </label>
                    <input
                      id="template-sort-order"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        handleInputChange(
                          'sort_order',
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) =>
                          handleInputChange('active', e.target.checked)
                        }
                        className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Active (visible to users)
                    </label>
                  </div>
                </div>

                {formData.preview_image_url.trim() && (
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div
                      className="h-16 w-24 overflow-hidden rounded-md border border-gray-200 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${formData.preview_image_url})`,
                      }}
                    />
                    <div className="text-xs text-gray-500">
                      Preview of the template card image.
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving
                    ? 'Saving...'
                    : editing
                      ? 'Save Changes'
                      : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeDelete}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close delete template modal"
              disabled={isDeleting}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              Delete Template
            </h2>
            <p className="text-sm text-gray-500">
              Are you sure you want to remove this template? This will no longer
              be available for users.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDelete}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisionBoardTemplates;
